/**
 * BillingSettings - Consultant billing management component
 * 
 * Features:
 * - View subscription status
 * - Subscribe to platform ($49/month)
 * - Purchase credit packs
 * - View credit balance and history
 * - Manage subscription via Stripe portal
 * - Configure default billing settings
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  ShoppingCart as ShoppingCartIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { billingAPI, BillingStatus, CreditBalanceResponse, ConsultantSettingsResponse } from '../../services/api';
import { useBilling } from '../../contexts/BillingContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useConsultantBranding } from '../../contexts/ConsultantBrandingContext';

// Credit pack pricing
const CREDIT_PACKS = [
  { 
    type: 'starter' as const, 
    name: 'Starter Pack', 
    credits: 3, 
    price: 249,
    description: 'Perfect for getting started',
    icon: <StarIcon />,
  },
  { 
    type: 'professional' as const, 
    name: 'Professional Pack', 
    credits: 10, 
    price: 699,
    description: 'Best value for growing businesses',
    icon: <RocketIcon />,
    popular: true,
  },
  { 
    type: 'scale' as const, 
    name: 'Scale Pack', 
    credits: 30, 
    price: 1499,
    description: 'For high-volume consulting',
    icon: <BusinessIcon />,
  },
];

const BillingSettings: React.FC = () => {
  const { billingStatus, refreshBilling, isAlwaysFree } = useBilling();
  const { consultantCompanies } = useCompany();
  const { branding, refreshBranding } = useConsultantBranding();
  const [creditBalance, setCreditBalance] = useState<CreditBalanceResponse | null>(null);
  const [consultantSettings, setConsultantSettings] = useState<ConsultantSettingsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Branding form state
  const [brandingForm, setBrandingForm] = useState({
    company_display_name: '',
    logo_url: '',
    company_tagline: '',
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  
  // Get the consultant company ID (first consultant company the user belongs to)
  const consultantCompanyId = consultantCompanies?.[0]?.id;

  // Load billing data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [credits, settings] = await Promise.all([
          billingAPI.getCreditBalance(),
          billingAPI.getConsultantSettings(),
        ]);
        setCreditBalance(credits);
        setConsultantSettings(settings);
        
        // Initialize branding form from settings
        if (settings) {
          setBrandingForm({
            company_display_name: settings.company_display_name || '',
            logo_url: settings.logo_url || '',
            company_tagline: settings.company_tagline || '',
          });
        }
      } catch (err: any) {
        console.error('Error loading billing data:', err);
        setError(err.message || 'Failed to load billing data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);
  
  // Sync branding form when branding context updates
  useEffect(() => {
    if (branding) {
      setBrandingForm({
        company_display_name: branding.company_display_name || '',
        logo_url: branding.logo_url || '',
        company_tagline: branding.company_tagline || '',
      });
    }
  }, [branding]);

  // Handle subscription
  const handleSubscribe = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      const { checkout_url } = await billingAPI.createCheckoutSession('subscription', consultantCompanyId);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  // Handle credit pack purchase
  const handlePurchaseCredits = async (packType: 'starter' | 'professional' | 'scale') => {
    try {
      setIsProcessing(true);
      setError(null);
      const { checkout_url } = await billingAPI.createCheckoutSession(packType, consultantCompanyId);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  // Handle manage subscription
  const handleManageSubscription = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      const { portal_url } = await billingAPI.createPortalSession();
      window.location.href = portal_url;
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal');
      setIsProcessing(false);
    }
  };

  // Handle settings update
  const handleSettingsChange = async (field: string, value: any) => {
    try {
      setError(null);
      await billingAPI.updateConsultantSettings({ [field]: value });
      setConsultantSettings(prev => prev ? { ...prev, [field]: value } : null);
      setSuccessMessage('Settings updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
    }
  };
  
  // Handle branding save
  const handleSaveBranding = async () => {
    try {
      setIsSavingBranding(true);
      setError(null);
      await billingAPI.updateConsultantSettings(brandingForm);
      await refreshBranding(); // Refresh branding in header
      setSuccessMessage('Branding settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save branding settings');
    } finally {
      setIsSavingBranding(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Branding Section Component (reusable for both free and paid accounts)
  const BrandingSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon /> Company Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your company name and logo will appear in the header alongside CHECK SET GO branding.
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Company Display Name"
            value={brandingForm.company_display_name}
            onChange={(e) => setBrandingForm({ ...brandingForm, company_display_name: e.target.value })}
            helperText="Your company name displayed in the header (e.g., 'Acme Consulting')"
            placeholder={consultantCompanies?.[0]?.name || 'Your Company Name'}
          />
          
          <TextField
            fullWidth
            label="Logo URL"
            value={brandingForm.logo_url}
            onChange={(e) => setBrandingForm({ ...brandingForm, logo_url: e.target.value })}
            helperText="URL to your company logo (recommended: square or horizontal logo, PNG/SVG with transparent background)"
            placeholder="https://example.com/logo.png"
          />
          
          <TextField
            fullWidth
            label="Company Tagline"
            value={brandingForm.company_tagline}
            onChange={(e) => setBrandingForm({ ...brandingForm, company_tagline: e.target.value })}
            helperText="Optional tagline shown below your company name"
            placeholder="Your trusted audit partner"
          />
          
          {/* Preview */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Header Preview</Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              p: 1.5,
              backgroundColor: 'primary.main',
              borderRadius: 1,
              maxWidth: 400,
            }}>
              {/* Consultant Logo */}
              {brandingForm.logo_url ? (
                <Box
                  component="img"
                  src={brandingForm.logo_url}
                  alt={brandingForm.company_display_name || 'Company Logo'}
                  sx={{
                    height: 28,
                    maxWidth: 120,
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)',
                  }}
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
              ) : brandingForm.company_display_name ? (
                // Only show initial if there's a company name but no logo
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                  }}
                >
                  {brandingForm.company_display_name.charAt(0)}
                </Box>
              ) : null}
              
              {/* Consultant Name - only show if there's a name */}
              {brandingForm.company_display_name && (
                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                  <Typography 
                    sx={{ 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {brandingForm.company_display_name}
                  </Typography>
                  {brandingForm.company_tagline && (
                    <Typography 
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.6rem',
                        lineHeight: 1,
                      }}
                    >
                      {brandingForm.company_tagline}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Show placeholder if nothing is configured */}
              {!brandingForm.logo_url && !brandingForm.company_display_name && (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                  No branding configured
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveBranding}
              disabled={isSavingBranding}
              startIcon={isSavingBranding ? <CircularProgress size={20} /> : null}
            >
              Save Branding
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // Always free accounts see a special message
  if (isAlwaysFree) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🎉 Unlimited Access Account
          </Typography>
          <Typography>
            Your account has unlimited access to all platform features. No subscription or credits required.
          </Typography>
        </Alert>

        {/* Branding Section */}
        <BrandingSection />

        {/* Settings section still shown */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon /> Default Settings
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            <Stack spacing={3}>
              {/* Default billing responsibility */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  Who pays for audits by default?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  This default applies to new client companies. Can be overridden per client.
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={consultantSettings?.default_billing_responsibility ?? 'consultant'}
                    onChange={(e) => handleSettingsChange('default_billing_responsibility', e.target.value)}
                  >
                    <FormControlLabel
                      value="consultant"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">Consultant Pays</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Credits are deducted from your shared pool
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="client"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">Client Pays</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Each client purchases their own credits
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Divider />

              {/* Self-service setting */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={consultantSettings?.allow_client_self_service ?? false}
                      onChange={(e) => handleSettingsChange('allow_client_self_service', e.target.checked)}
                    />
                  }
                  label="Allow clients to run their own audits by default"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  This can be overridden per client in their company settings.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Subscription Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCardIcon /> Platform Subscription
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  icon={billingStatus?.subscription_status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
                  label={billingStatus?.subscription_status?.toUpperCase() || 'UNKNOWN'}
                  color={
                    billingStatus?.subscription_status === 'active' ? 'success' :
                    billingStatus?.subscription_status === 'trial' ? 'info' :
                    billingStatus?.subscription_status === 'grace' ? 'warning' : 'error'
                  }
                  sx={{ mt: 0.5 }}
                />
                
                {billingStatus?.subscription_status === 'trial' && billingStatus?.trial_days_remaining !== undefined && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {billingStatus.trial_days_remaining} days remaining in trial
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Box textAlign={{ xs: 'left', md: 'right' }}>
                {billingStatus?.subscription_status === 'active' ? (
                  <Button
                    variant="outlined"
                    onClick={handleManageSubscription}
                    disabled={isProcessing}
                  >
                    Manage Subscription
                  </Button>
                ) : (
                  <Box>
                    <Typography variant="h4" color="primary" gutterBottom>
                      $49<Typography component="span" variant="body1">/month</Typography>
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubscribe}
                      disabled={isProcessing}
                      startIcon={isProcessing ? <CircularProgress size={20} /> : <CreditCardIcon />}
                    >
                      Subscribe Now
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Credit Balance */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon /> Review Credits
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box textAlign="center" p={2} bgcolor="action.hover" borderRadius={2}>
                <Typography variant="h2" color="primary">
                  {creditBalance?.balance ?? 0}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Credits Available
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle2" gutterBottom>Purchase History</Typography>
              {creditBalance?.purchases && creditBalance.purchases.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pack</TableCell>
                        <TableCell align="center">Remaining</TableCell>
                        <TableCell align="right">Expires</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creditBalance.purchases.map((purchase) => (
                        <TableRow key={purchase.id}>
                          <TableCell>
                            {purchase.pack_type.charAt(0).toUpperCase() + purchase.pack_type.slice(1)}
                          </TableCell>
                          <TableCell align="center">
                            {purchase.credits_remaining} / {purchase.credits_purchased}
                          </TableCell>
                          <TableCell align="right">
                            {formatDate(purchase.expires_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No credit purchases yet.
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Credit Packs */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Purchase Credit Packs
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Credits expire 2 years from purchase. All AI features included.
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            {CREDIT_PACKS.map((pack) => (
              <Grid size={{ xs: 12, md: 4 }} key={pack.type}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    border: pack.popular ? 2 : 1,
                    borderColor: pack.popular ? 'primary.main' : 'divider',
                  }}
                >
                  {pack.popular && (
                    <Chip 
                      label="Most Popular" 
                      color="primary" 
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: -12, 
                        left: '50%', 
                        transform: 'translateX(-50%)',
                      }}
                    />
                  )}
                  <CardContent sx={{ textAlign: 'center', pt: pack.popular ? 3 : 2 }}>
                    <Box color="primary.main" sx={{ mb: 1 }}>
                      {pack.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {pack.name}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      ${pack.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {pack.credits} audit credits
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {pack.description}
                    </Typography>
                    <Button
                      variant={pack.popular ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={() => handlePurchaseCredits(pack.type)}
                      disabled={isProcessing || billingStatus?.subscription_status !== 'active'}
                    >
                      Purchase
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {billingStatus?.subscription_status !== 'active' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You need an active subscription to purchase credit packs.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Company Branding */}
      <BrandingSection />

      {/* Default Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon /> Default Settings
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Stack spacing={3}>
            {/* Default billing responsibility */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Who pays for audits by default?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This default applies to new client companies. Can be overridden per client.
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={consultantSettings?.default_billing_responsibility ?? 'consultant'}
                  onChange={(e) => handleSettingsChange('default_billing_responsibility', e.target.value)}
                >
                  <FormControlLabel
                    value="consultant"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Consultant Pays</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Credits are deducted from your shared pool
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="client"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Client Pays</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Each client purchases their own credits
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider />

            {/* Self-service setting */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={consultantSettings?.allow_client_self_service ?? false}
                    onChange={(e) => handleSettingsChange('allow_client_self_service', e.target.checked)}
                  />
                }
                label="Allow clients to run their own audits by default"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                This can be overridden per client in their company settings.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingSettings;

